import { useState, useEffect, useCallback } from "react";
import { Send, Loader2, Trash2, MessageSquare, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
}

export default function Community({ user }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/comments");
      setComments(data);
    } catch {
      toast.error("Couldn't load the community feed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const value = text.trim();
    if (!value) return;
    setPosting(true);
    try {
      const { data } = await api.post("/comments", { text: value });
      setComments((c) => [data, ...c]);
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post your comment");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
    } catch {
      toast.error("Couldn't delete comment");
    }
  };

  return (
    <div className="af-fade-up max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Community</h1>
        <span className="flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-widest text-brand bg-brand-soft border border-brand/30 px-2 py-0.5 rounded-full">
          <Users className="w-3 h-3" /> Job seekers
        </span>
      </div>
      <p className="text-body mb-6">Swap tips, wins and encouragement with other job seekers.</p>

      {/* Composer */}
      <div className="rounded-xl border border-line bg-surface p-4 mb-6">
        <textarea
          data-testid="comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          rows={3}
          placeholder="Share an interview tip, a win, or ask the community…"
          className="w-full bg-canvas border border-line-strong rounded-lg px-4 py-3 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors resize-y"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-faint font-mono2">{text.length}/1000</span>
          <button
            data-testid="post-comment-button"
            onClick={post}
            disabled={posting || !text.trim()}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-50"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
          </button>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-brand animate-spin" /></div>
      ) : comments.length === 0 ? (
        <div className="rounded-xl border border-line bg-surface p-10 text-center text-body">
          <MessageSquare className="w-8 h-8 mx-auto mb-3 text-faint" />
          Be the first to post something!
        </div>
      ) : (
        <div className="space-y-3" data-testid="comment-feed">
          {comments.map((c) => (
            <div key={c.id} data-testid={`comment-item-${c.id}`} className="rounded-xl border border-line bg-surface p-4 flex gap-3">
              {c.author_picture ? (
                <img src={c.author_picture} alt="" className="w-9 h-9 rounded-full border border-line shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-brand-soft border border-brand/30 text-brand flex items-center justify-center text-xs font-bold shrink-0">
                  {initials(c.author_name)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{c.author_name}</span>
                  <span className="text-xs text-faint font-mono2">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-sm text-body mt-1 whitespace-pre-wrap break-words">{c.text}</p>
              </div>
              {c.user_id === user?.user_id && (
                <button
                  data-testid={`delete-comment-${c.id}`}
                  onClick={() => remove(c.id)}
                  className="p-2 h-8 rounded-lg text-faint hover:text-danger hover:bg-subtle transition-colors shrink-0"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
